import type { ReactNode } from "react"
import React from "react"

import type { Background } from "@/lib/background"
import { BackgroundContext } from "@/lib/background/context"
import type { Loading } from "@/lib/loading"
import { ReactPerformance } from "@/lib/react_performance"
import type { Subscription } from "@/lib/subscription"

type Props = {
  children: ReactNode
  loading: Loading.ContextData
}

type State = {
  ready: boolean
  update: boolean
}

export class BackgroundContainerComponent extends React.Component<
  Props,
  State
> {
  private listeners: Record<
    Subscription.ListenerId,
    Background.Functions.Handler
  > = {}

  public state: State = {
    ready: false,
    update: false,
  }

  public componentDidMount = () => {
    this.onReady()
  }

  private onBackgroundClick: React.MouseEventHandler<HTMLDivElement> = async (
    event
  ) => {
    await Promise.all(
      Object.values(this.listeners).map((listener) => {
        return listener(event)
      })
    )
  }

  private onReady = () => {
    this.props.loading.update({
      background: { loading: false },
    })
    this.setState({ ready: true })
  }

  public render = () => {
    return (
      <BackgroundContext.Provider
        value={{
          subscribeToBackgroundClicks: this.subscribeToBackgroundClicks,
        }}
      >
        <div onClick={this.onBackgroundClick}>{this.props.children}</div>
      </BackgroundContext.Provider>
    )
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    Props,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps }) => {
      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private subscribeToBackgroundClicks: Background.Functions.Subscribe = ({
    onBackgroundClick,
    listenerId,
  }) => {
    this.listeners[listenerId] = onBackgroundClick

    return () => {
      const { [listenerId]: _, ...rest } = this.listeners

      this.listeners = rest
    }
  }
}
